import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { samlPost } from '../../../functions/saml-post';
import { ReportsService } from '../../../services/reports/reports.service';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { IconButtonComponent } from '../../core/buttons/icon-button.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { ConfirmTabDialogComponent } from '../../core/panels/confirm-tab-dialog.component';
import { TabPanelComponent } from '../../core/panels/tab-panel/tab-panel.component';
import { TabComponent } from '../../core/panels/tab-panel/tab.component';
import { CompletedReportsComponent } from '../completed-report/completed-reports.component';
import { ReportPrefDialogComponent } from '../core/report-pref-dialog.component';
import { DistributedReportsComponent } from '../distributed-report/distributed-reports.component';
import { MyFiltersComponent } from '../my-filter/my-filters.component';
import { MyReportsComponent } from '../my-report/my-reports.component';
import { PendingFiltersComponent } from '../pending-filter/pending-filters.component';
import { PendingReportsComponent } from '../pending-report/pending-reports.component';
import { ScheduledReportsComponent } from '../scheduled-report/scheduled-reports.component';
import { HomeCardComponent } from '../../home/home-card.component';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';

@Component({
  selector: 'app-reports-landing',
  imports: [
    TabPanelComponent,
    TabComponent,
    CompletedReportsComponent,
    MyReportsComponent,
    ScheduledReportsComponent,
    PageHeaderComponent,
    ButtonComponent,
    PendingReportsComponent,
    MyFiltersComponent,
    PendingFiltersComponent,
    IconButtonComponent,
    ReportPrefDialogComponent,
    DistributedReportsComponent,
    ConfirmTabDialogComponent,
    HomeCardComponent,
    LoadingPanelComponent,
  ],
  template: `
    <app-page-header
      [label]="isFilterOnly && !hasDistributedReports ? 'Filters' : 'Reports'"
      headerSize="h1"
    >
      <ng-template #right>
        <div class="tw-flex tw-flex-wrap tw-gap-2">
          @if (!isFilterOnly && !isDistributedOnly) {
            <app-button label="New report" (click)="newReport()" />
          }
          @if (!isFilterOnly && !isDistributedOnly && hasAdvancedAnalytics) {
            <app-button
              label="Advanced analytics"
              emphasis="Low"
              [loading]="isLoadingSso"
              (click)="openAdvancedAnalytics()"
            />
          }
          <app-icon-button
            [border]="false"
            [ariaLabel]="
              isFilterOnly ? 'Filter preferences' : 'Report preferences'
            "
            [tooltip]="
              isFilterOnly ? 'Filter preferences' : 'Report preferences'
            "
            icon="fa-light fa-gear"
            (click)="showPreferences()"
          />
        </div>
      </ng-template>
    </app-page-header>
    <div class="tw-grow tw-w-full tw-justify-center">
      <app-home-card type="Large">
        <div
          class="tw-pb-8 tw-w-full tw-min-w-0 tw-overflow-x-auto"
          style="contain: inline-size;"
        >
          <app-loading-panel
            [state]="loading ? 'Loading' : 'Loaded'"
            loadingStyle="Box"
            [loadingHeight]="500"
          >
            <ng-template #loaded>
              <app-tab-panel
                [value]="tab"
                activeTabQueryParam="tab"
                (valueChange)="tab = $event"
              >
                @if (!isFilterOnly && !isDistributedOnly) {
                  <app-tab label="Personalized" value="reports">
                    <ng-template #content>
                      <app-my-reports />
                    </ng-template>
                  </app-tab>
                  <app-tab label="Scheduled" value="scheduled">
                    <ng-template #content
                      ><app-scheduled-reports
                    /></ng-template>
                  </app-tab>
                }
                @if (
                  !isFilterOnly || isDistributedOnly || hasDistributedReports
                ) {
                  <app-tab label="Completed" value="completed">
                    <ng-template #content>
                      @if (!isFilterOnly && !isDistributedOnly) {
                        <app-completed-reports />
                      }
                      @if (hasDistributedReports) {
                        <app-distributed-reports />
                      }
                      @if (hasCompletedReports || hasDistributedReports) {
                        <div class="tw-py-8 tw-italic tw-text-sm">
                          Please note: Reports will be available for 120 days
                          after the requested date.
                        </div>
                      }
                      @if (hasDistributedReports) {
                        <div class="tw-italic tw-text-sm">
                          Distributed reports are reports that have been shared
                          with you by another user through email.
                        </div>
                      }
                    </ng-template>
                  </app-tab>
                }

                @if (!isDistributedOnly) {
                  <app-tab label="Filters" value="filters">
                    <ng-template #content><app-my-filters /> </ng-template>
                  </app-tab>
                }
                @if (!isFilterOnly && !isDistributedOnly && hasPendingReports) {
                  <app-tab
                    label="Shared reports"
                    value="pending-reports"
                    icon="fa-inbox-in fa-regular"
                  >
                    <ng-template #content>
                      <app-pending-reports />
                    </ng-template>
                  </app-tab>
                }
                @if (showSharedFiltersTab) {
                  <app-tab
                    label="Shared filters"
                    value="pending-filters"
                    icon="fa-inbox-in fa-regular"
                  >
                    <ng-template #content>
                      <app-pending-filters />
                    </ng-template>
                  </app-tab>
                }
              </app-tab-panel>
            </ng-template>
          </app-loading-panel>
        </div>
      </app-home-card>
    </div>
    <app-report-pref-dialog #prefDialog />
    <app-confirm-tab-dialog
      #confirmTabDialog
      (confirm)="onAdvancedAnalyticsConfirmed()"
    />
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
      max-width: 100vw;
      overflow: hidden;
    }
  `,
})
export class ReportsLandingComponent extends BaseComponent implements OnInit {
  protected reports = inject(ReportsService);

  @ViewChild('prefDialog')
  prefDialog: ReportPrefDialogComponent;

  @ViewChild('confirmTabDialog')
  confirmTabDialog: ConfirmTabDialogComponent;

  hasPendingReports = false;
  hasDistributedReports = false;
  hasCompletedReports = false;
  hasAdvancedAnalytics = false;
  isLoadingSso = false;
  isFilterOnly = false;
  isDistributedOnly = false;
  /** Always show when Filters tab is shown — matches Classic filters deeplink reliability. */
  showSharedFiltersTab = false;

  constructor() {
    super();
    this.isFilterOnly =
      !this.session.authorize('ReportsModule') &&
      this.session.authorize('FiltersModule');
    this.isDistributedOnly =
      !this.session.authorize('ReportsModule') &&
      !this.session.authorize('FiltersModule') &&
      this.session.authorize('DistributedReports');
    this.hasAdvancedAnalytics = this.session.authorize('AdvancedAnalytics');
    this.showSharedFiltersTab =
      !this.isDistributedOnly &&
      (this.session.authorize('ReportsModule') ||
        this.session.authorize('FiltersModule'));
  }

  loading = true;
  tab: string;

  private syncDefaultTabToQueryString(): void {
    if (this.activatedRoute.snapshot.queryParamMap.has('tab') || !this.tab)
      return;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { tab: this.tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  async ngOnInit() {
    this.reports.clearReport();

    this.reports.startPolling();

    this.dispose.subs = this.reports.reportsLoaded.subscribe((result) => {
      this.hasPendingReports = result.allReports?.pendingRequests?.length > 0;
      this.hasDistributedReports = result.distributedReports?.length > 0;
      this.hasCompletedReports =
        result.allReports?.completedRequests?.length > 0;
    });

    this.dispose.subs = this.activatedRoute.queryParams.subscribe((params) => {
      if (params['tab']) {
        this.tab = params['tab'];
      }
    });

    if (this.isDistributedOnly) {
      await this.reports.getExternalReports();
    } else {
      await this.reports.getReports();
    }

    if (!this.tab) {
      this.tab =
        this.isDistributedOnly ||
        (this.isFilterOnly && this.hasDistributedReports)
          ? 'completed'
          : this.isFilterOnly
            ? 'filters'
            : 'reports';
    }
    this.syncDefaultTabToQueryString();
    this.loading = false;
  }

  showPreferences() {
    this.prefDialog.show();
  }

  newReport() {
    this.reports.initializeNewReport();
    this.reports.setMode('new');
    this.router.navigate(['reports/new-report']);
  }

  openAdvancedAnalytics(): void {
    this.confirmTabDialog.show();
  }

  async onAdvancedAnalyticsConfirmed(): Promise<void> {
    if (this.isLoadingSso) {
      return;
    }

    this.isLoadingSso = true;

    try {
      const { sessionId, userName } = this.session.user;

      const request = {
        sessionId,
        userId: userName,
        target: 'ADVANCED_ANALYTICS' as const,
      };

      const response = await this.api.sso.generateAdvancedAnalyticsSso(request);

      if (response.success) {
        samlPost(
          response.samlResponse,
          response.targetUrl,
          'SAMLResponse',
          response.relayState,
        );
      }
    } finally {
      this.isLoadingSso = false;
    }
  }

  override ngOnDestroy() {
    this.reports.stopPolling();
    super.ngOnDestroy();
  }
}
